import { useQuery } from "@tanstack/react-query";
import { useListEnrollments } from "./useEnrollments";
import { ParticipantsService } from "@/services/participants.service";
import {
  DisplayTransaction,
  transformEnrollmentTransaction,
  transformRedeemedItem,
} from "@/utils/transaction.utils";
import { Transaction, Reward } from "@/domain/enrollment.interface";

interface UseTransactionHistoryParams {
  participantId: string;
  userId: string;
  enabled?: boolean;
}

export const useTransactionHistory = ({
  participantId,
  userId,
  enabled = true,
}: UseTransactionHistoryParams) => {
  // Fetch all enrollments
  const {
    enrollments,
    isLoadingEnrollments,
    isErrorEnrollments,
    refetchEnrollments,
  } = useListEnrollments(participantId, userId, enabled);

  // Fetch redeemed items
  const {
    data: redeemedItems,
    isLoading: isLoadingRedeemed,
    isError: isErrorRedeemed,
    refetch: refetchRedeemed,
  } = useQuery({
    queryKey: ["redeemed-items", participantId],
    queryFn: () => ParticipantsService.getRedeemedItems(participantId),
    staleTime: 1 * 60 * 60 * 1000,
    enabled,
  });

  // Process and combine transactions
  const transactions: DisplayTransaction[] = (() => {
    if (!enrollments && !redeemedItems) return [];

    const allTransactions: DisplayTransaction[] = [];

    // Extract transactions from enrollments
    if (enrollments) {
      enrollments.forEach((enrollment) => {
        if (enrollment.balance?.transactions) {
          enrollment.balance.transactions.forEach(
            (transaction: Transaction) => {
              // Filter out transactions with amount === 0
              if (transaction.amount !== 0) {
                allTransactions.push(
                  transformEnrollmentTransaction(transaction),
                );
              }
            },
          );
        }
      });
    }

    // Add redeemed items as negative transactions
    if (redeemedItems && Array.isArray(redeemedItems)) {
      redeemedItems.forEach((reward: Reward) => {
        // Filter out rewards with amount === 0
        if (reward.amount !== 0) {
          allTransactions.push(transformRedeemedItem(reward));
        }
      });
    }

    // Sort by date (newest first)
    return allTransactions.sort((a, b) => {
      return (
        new Date(b.originalDate).getTime() - new Date(a.originalDate).getTime()
      );
    });
  })();

  const isLoading = isLoadingEnrollments || isLoadingRedeemed;
  const isError = isErrorEnrollments || isErrorRedeemed;

  const refetch = () => {
    refetchEnrollments();
    refetchRedeemed();
  };

  return {
    transactions,
    isLoading,
    isError,
    refetch,
  };
};
